<template>
  <div
    ref="listRoot"
    class="relative h-full"
    @dragenter="onDragEnter"
    @dragover="onDragOver"
    @dragleave="onDragLeave"
    @drop="onDrop"
  >
    <transition-group name="f-slide-fade">
      <core-state-layout v-if="error" key="error">
        <core-contextual-error-box :action="refresh" />
      </core-state-layout>

      <file-list-grid
        key="list-grid"
        v-else
        :files="files"
        :files-store-id="filesStoreId"
        :group-files="sortOption.isGrouped && !collectionDescriptor?.search"
        :disable-approach-bottom="isPageQueued || !hasMore"
        :disable-selection="disableSelection"
        :disable-context-menu="disableContextMenu"
        :disable-back-to-top="disableBackToTop"
        :get-group-files="getGroupFiles"
        @approach-bottom="getMore"
        @list-item-click="item => emit('list-item-click', {item})"
      >

        <template #header>
          <slot name="header" />
        </template>

        <template #under-header>

          <!-- note: this needs to use v-show because otherwise there are temporary states that cause file-list-control and by extension file-filter-panel to be unmounted and remounted -->
          <div v-show="initialResolved && (!isEmpty || isEmptySearch) && !disableToolbar" class="z-10 mr-[1px] bg-white pr-[2px] lg:mr-[18px] lg:pr-[5px] isolate transition-none sticky top-0">
            <file-list-controls
              class="pb-2"
              :files-store-id="filesStoreId"
              :context-type="contextType"
              :context="context"
            >
              <slot name="controls" />
            </file-list-controls>

            <div
              class="absolute -inset-0.5 bg-white/50 backdrop-blur-[2px] z-10 transition-opacity duration-300"
              :class="selection.hasAny.value ? 'opacity-1 pointer-events-auto': 'opacity-0 pointer-events-none'"
            />
          </div>

          <div v-if="isEmptySearch && !centerEmptyState">
            <core-empty-state
              heading="No Results"
              description="No files were found that match your search criteria."
              :icon="COMMON_ICONS.noFiles"
            />
          </div>

          <div v-else-if="isEmpty && !centerEmptyState">
            <slot name="empty-state">
              <core-empty-state
                heading="No Files"
                description="No files in this container."
                :icon="COMMON_ICONS.noFiles"
              />
            </slot>
          </div>

          <div v-if="centerEmptyState" ref="emptyStateBoundary" />

        </template>
      </file-list-grid>
    </transition-group>

    <core-empty-state-overlay
      v-if="centerEmptyState && isEmpty && !error"
      :container="listRoot"
      :boundary="emptyStateBoundary"
    >
      <core-empty-state
        v-if="isEmptySearch"
        heading="No Results"
        description="No files were found that match your search criteria."
        :icon="COMMON_ICONS.noFiles"
      />
      <slot v-else name="empty-state">
        <core-empty-state
          heading="No Files"
          description="No files in this container."
          :icon="COMMON_ICONS.noFiles"
        />
      </slot>
    </core-empty-state-overlay>

    <transition name="f-slide-fade">
      <div
        v-if="isCollectionLoading"
        class="absolute inset-y-0 left-0 z-20 bg-white/85 backdrop-blur-[2px] dark:bg-charcoal-900/85"
        :class="insetLoadingOverlay ? 'right-[9px] lg:right-[29px]' : 'right-0'"
      >
        <core-state-layout>
          <core-contextual-loading-box :loading-text="loadingText" />
        </core-state-layout>
      </div>
    </transition>

    <div
      v-if="isDragOverlayVisible"
      class="pointer-events-none absolute inset-y-0 left-0 right-[9px] lg:right-[29px] z-20 rounded-xl border-2 border-dashed border-charcoal-400/80 bg-black/20 backdrop-blur-[4px] flex flex-col items-center justify-center gap-3"
    >
      <u-icon :name="COMMON_ICONS.upload" class="text-heroblue-500 h-14 w-14" />
      <p class="text-white font-serif text-3xl">Drop files or folders to upload</p>
    </div>
  </div>
</template>

<script setup>
  import {storeToRefs} from 'pinia';
  import {makeFilesStore} from '~/stores/files.js';
  import {getDroppedFiles} from '~/composables/upload/local-drop-files.js';

  const emit = defineEmits(['list-item-click', 'empty-state-change']);
  const props = defineProps({
    filesStoreId: String,
    userId: String,
    context: Object,
    contextType: String,
    shareToken: String,
    subcontext: Object,
    addedSince: String,
    disableSelection: Boolean,
    disableToolbar: Boolean,
    centerEmptyState: Boolean,
    insetLoadingOverlay: {
      type: Boolean,
      default: true
    },
    enableLocalFileDropUpload: Boolean,
    uploadModalProps: {
      type: Object,
      default: () => ({})
    },
    loadingText: {
      type: String,
      default: 'Loading Files'
    },
    disableContextMenu: Boolean,
    disableBackToTop: Boolean,
    scopeDateRange: Object,
    scopeFileTypes: Array
  });

  const listRoot = ref(null);
  const emptyStateBoundary = ref(null);

  const selection = useSelection();
  const filesStore = props.filesStoreId
    ? makeFilesStore(props.filesStoreId)()
    : useFilesStore();

  const {files, hasMore, isBuffering, isPageQueued, collectionDescriptor, areFiltersApplied, isLoadingFiles} = storeToRefs(filesStore);
  const sortOption = computed(() => collectionDescriptor.value?.order || FILE_DEFAULT_SORT_OPTION);

  const route = useRoute();
  const router = useRouter();
  const storeFiltersState = inject('storeFiltersState', false);

  const {refresh, error, pending, status: loadStatus} = await useLazyAsyncData(
    `files-${Math.random().toString(36).substring(7)}`,
    async () => {
      if (props.context?.id
        && collectionDescriptor.value?.contextId === props.context?.id
        && props.addedSince === collectionDescriptor.value?.addedSince) {

        //note: this has already been loaded - no need to reload
        return;
      }

      const hasExplicitCollectionQuery = [
        'order',
        'filter',
        'dateRange',
        'organization'
      ].some(key => route.query?.[key] != null);

      const order = (() => {
        if (props.contextType === FILE_CONTEXTS.deleteBin) {
          return FILE_SORT_OPTION_DELETED;
        }

        if (props.contextType === FILE_CONTEXTS.timeline) {
          return FILE_DEFAULT_SORT_OPTION;
        }

        if (props.context) {
          return FILE_SORT_OPTIONS_WITH_CUSTOM.find(so => so.paramName === props.context.file_sort) || FILE_DEFAULT_SORT_OPTION;
        }

        const qsSort = route.query?.order;

        if (qsSort) {
          return FILE_SORT_OPTIONS_WITH_CUSTOM.find(so => so.paramName === qsSort);
        }

        return FILE_DEFAULT_SORT_OPTION;
      })();

      const organization = [];

      if (route.query?.filter === 'favorites') {
        organization.push(FILE_FILTERS.favorites);
      }

      if (route.query?.organization) {
        organization.push(...JSON.parse(route.query.organization));
      }

      const dateRange = props.contextType === FILE_CONTEXTS.timeline
        ? null
        : route.query?.dateRange && JSON.parse(route.query.dateRange);

      router.replace({
        query: {
          ...route.query,
          order: undefined,
          filter: undefined,
          dateRange: undefined,
          organization: undefined
        }
      });

      return await filesStore.getFiles({
        order,
        organization,
        dateRange,
        scopeDateRange: props.scopeDateRange,
        scopeFileTypes: props.scopeFileTypes,
        userId: props.userId,
        shareToken: props.shareToken,
        contextType: props.contextType,
        context: props.context,
        subcontext: props.subcontext,
        addedSince: props.addedSince,
        useStoredCollection: storeFiltersState && !hasExplicitCollectionQuery
      });
    },
    {
      server: false,
      watch: [() => props.addedSince]
    }
  );

  const {initialResolved, initialLoading} = useInitialLoadState(loadStatus);
  const isLoading = computed(() => initialLoading.value || pending.value || isLoadingFiles.value);
  const isCollectionLoading = computed(() => (
    initialLoading.value || (isLoading.value && !collectionDescriptor.value?.matchesLoadedFiles)
  ));
  const isEmpty = computed(() => !files.value?.length && !isLoading.value);
  const isEmptySearch = computed(() => isEmpty.value && (collectionDescriptor.value?.search || areFiltersApplied.value));
  watch([isLoading, error, isEmpty, isEmptySearch, initialResolved], ([loading, loadError, empty, emptySearch, resolved]) => {
    emit('empty-state-change', {
      initialResolved: resolved,
      pending: loading,
      failed: Boolean(loadError),
      empty: !loading && !loadError && empty && !emptySearch,
      emptySearch: !loading && !loadError && Boolean(emptySearch)
    });
  }, {immediate: true});
  const isDropUploadEnabled = computed(() => props.enableLocalFileDropUpload && process.client);
  const dragEnterCount = ref(0);
  const isDragOverlayVisible = ref(false);

  const getMoreError = ref(null);

  async function getMore() {
    if (!hasMore.value) {
      return;
    }

    try {
      await filesStore.getFiles({nextPage: true}); //todo: see if we can remember sort for subsequent pages
    } catch (e) {
      getMoreError.value = e; //todo: what do we do?
    }
  }

  const getGroupFiles = async ({groupId}) => {
    const getFileGroupId = file => file.group?.id || '__ungrouped__';
    const groupFiles = computed(() => files.value?.filter(file => getFileGroupId(file) === groupId));

    while(hasMore.value && (files.value.findLastIndex(file => getFileGroupId(file) === groupId) === files.value.length - 1) && groupFiles.value.length < FILE_MAX_SELECTION) {

      filesStore.getFiles({nextPage: true}); //note: do not await this!!!

      await new Promise(resolve => {
        const theWatch = watch(isBuffering, newVal => {
          if (!newVal) {
            theWatch();
            resolve();
          }
        })
      });
    }

    return groupFiles.value;
  };

  function hasDraggedFiles(event) {
    return Array.from(event.dataTransfer?.types || []).includes('Files');
  }

  function onDragEnter(event) {
    if (!isDropUploadEnabled.value || !hasDraggedFiles(event)) {
      return;
    }

    event.preventDefault();
    dragEnterCount.value++;
    isDragOverlayVisible.value = true;
  }

  function onDragOver(event) {
    if (!isDropUploadEnabled.value || !hasDraggedFiles(event)) {
      return;
    }

    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
  }

  function onDragLeave(event) {
    if (!isDropUploadEnabled.value) {
      return;
    }

    event.preventDefault();
    dragEnterCount.value--;
    if (dragEnterCount.value <= 0) {
      dragEnterCount.value = 0;
      isDragOverlayVisible.value = false;
    }
  }

  async function onDrop(event) {
    if (!isDropUploadEnabled.value || !hasDraggedFiles(event)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    dragEnterCount.value = 0;
    isDragOverlayVisible.value = false;

    const initialFiles = await getDroppedFiles(event.dataTransfer);
    if (!initialFiles.length) {
      return;
    }

    await useUploadActions().openUploaderModal({
      ...props.uploadModalProps,
      initialFiles
    });
  }

</script>
